/**
 * Monthly caps per client team and studio (cowork engine/usageCaps.ts), as the client app shows them: "12 of 30 decks
 * this month". Staff are counted but never capped, so /v1/me gives them no caps and nothing is shown.
 */
import type { CapReachedBody, CapStatus, CapStudio } from './apiSignals';

export type CapUnit = 'decks' | 'edits' | 'minutes' | 'tracks' | 'exports' | 'previews' | 'autofills' | 'actions';

/** The units each studio counts (the shared contract). */
export const CAP_UNITS: Record<CapStudio, readonly CapUnit[]> = {
  deck: ['decks', 'edits'],
  voice: ['minutes'],
  audio: ['tracks'],
  video: ['exports', 'previews'],
  brand: ['autofills', 'actions'],
};

const UNIT_WORDS: Record<string, { one: string; many: string }> = {
  'deck:decks': { one: 'deck', many: 'decks' },
  'deck:edits': { one: 'deck edit', many: 'deck edits' },
  'voice:minutes': { one: 'minute of Lykke voice', many: 'minutes of Lykke voice' },
  'audio:tracks': { one: 'music track', many: 'music tracks' },
  'video:exports': { one: 'video export', many: 'video exports' },
  'video:previews': { one: 'video preview', many: 'video previews' },
  'brand:autofills': { one: 'Brand OS autofill', many: 'Brand OS autofills' },
  'brand:actions': { one: 'Brand OS action', many: 'Brand OS actions' },
};

export function unitWords(studio: string, unit: string, n: number): string {
  const w = UNIT_WORDS[`${studio}:${unit}`];
  if (!w) return unit;
  return n === 1 ? w.one : w.many;
}

export interface CapView extends CapStatus {
  remaining: number;
  /** Used up: the next action of this kind is refused until the month resets. */
  reached: boolean;
  /** 80% or more used. */
  near: boolean;
}

export function capFor(caps: readonly CapStatus[] | null | undefined, studio: string, unit: string): CapView | null {
  const c = (caps || []).find((x) => x.studio === studio && x.unit === unit);
  if (!c || !(c.limit >= 0)) return null;
  const used = Math.max(0, c.used || 0);
  const remaining = Math.max(0, c.limit - used);
  return { ...c, used, remaining, reached: used >= c.limit, near: c.limit > 0 && used / c.limit >= 0.8 };
}

/** "1 December" (the day the month's counts start again, Stockholm calendar). Empty when unknown. */
export function resetDay(resetsAt: string | null | undefined): string {
  if (!resetsAt) return '';
  const d = new Date(resetsAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'Europe/Stockholm' });
}

/** "12 of 30 decks this month". */
export function capLabel(c: Pick<CapStatus, 'studio' | 'unit' | 'limit' | 'used'>): string {
  return `${Math.min(c.used, c.limit)} of ${c.limit} ${unitWords(c.studio, c.unit, c.limit)} this month`;
}

/** What each studio is called in a sentence. */
export const STUDIO_NAMES: Record<string, string> = {
  deck: 'Deck Studio', voice: 'Lykke voice', audio: 'Audio Studio', video: 'Video Studio', brand: 'Brand OS',
};

/**
 * The message shown when a cap is reached: the server's own when it sends one, else a plain one. `fallbackStudio`
 * names the studio when the answer did not (an older helper that kept only "cap_reached").
 */
export function capReachedMessage(
  b: Pick<CapReachedBody, 'studio' | 'unit' | 'limit' | 'resetsAt' | 'message'>,
  fallbackStudio?: string,
): string {
  if (b.message && b.message.trim()) return b.message.trim();
  const day = resetDay(b.resetsAt);
  const studio = b.studio || fallbackStudio || '';
  const when = day ? ` It starts again on ${day}.` : ' It starts again next month.';
  let what: string;
  if (studio && b.unit && b.limit > 0) what = `Your team has used this month's ${b.limit} ${unitWords(studio, b.unit, b.limit)}.`;
  else if (STUDIO_NAMES[studio]) what = `Your team has reached this month's limit for ${STUDIO_NAMES[studio]}.`;
  else what = "Your team has reached this month's limit.";
  return `${what}${when} Ask your Beatly contact if you need more.`;
}
